/**
 * ProductSyncStatusLine — compact, production-honest one-line sync/channel
 * status row below the KPI cards. Pure presentational (all data in); only
 * renders wired facts (JTL last-sync/synced + real channel count).
 */
import type { ReactNode } from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ProductSyncStatusLineProps {
  /** Whether the sync pipeline is operating normally */
  syncHealthy: boolean;
  /** Pre-formatted (de-DE) last-sync timestamp from Products.tsx, or null */
  lastSyncLabel?: string | null;
  /** Number of products synchronised, or null when unknown */
  syncedCount?: number | null;
  /** Number of channels with computed readiness ("active") */
  activeChannels: number;
  /** Total number of configured channels */
  totalChannels: number;
  /** Channel-rules entry point (S4 <ChannelRulesEntry/>) injected here */
  channelRulesSlot?: ReactNode;
}

const Divider = () => (
  <span aria-hidden="true" className="text-neutral-300 dark:text-neutral-600">·</span>
);

export function ProductSyncStatusLine({
  syncHealthy,
  lastSyncLabel,
  syncedCount,
  activeChannels,
  totalChannels,
  channelRulesSlot,
}: ProductSyncStatusLineProps) {
  const { t } = useTranslation();
  const hasJtlInfo = !!lastSyncLabel || syncedCount != null;

  return (
    <div
      data-testid="sync-status-line"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-500 dark:text-neutral-400"
    >
      {syncHealthy ? (
        <span className="flex items-center gap-1">
          <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-emerald-500" />
          <span>{t('productsOverview.sync.healthy')}</span>
        </span>
      ) : (
        <span
          data-testid="sync-status-attention"
          className="flex items-center gap-1"
        >
          <AlertCircle aria-hidden="true" className="h-4 w-4 text-amber-500" />
          <span>{t('productsOverview.sync.attention')}</span>
        </span>
      )}

      {hasJtlInfo && (
        <>
          <Divider />
          <span className="font-medium text-neutral-700 dark:text-neutral-300">
            {t('productsOverview.sync.source')}
          </span>
          {lastSyncLabel && (
            <>
              <Divider />
              <span data-testid="sync-last-at">
                {t('productsOverview.sync.lastSyncAt', { date: lastSyncLabel })}
              </span>
            </>
          )}
          {syncedCount != null && (
            <>
              <Divider />
              <span data-testid="sync-synced-count">
                {t('productsOverview.sync.syncedShort', { count: syncedCount })}
              </span>
            </>
          )}
        </>
      )}

      <Divider />
      <span>
        {totalChannels === 0
          ? t('productsOverview.sync.noChannels')
          : t('productsOverview.sync.channelsActive', {
              active: activeChannels,
              total: totalChannels,
            })}
      </span>

      {channelRulesSlot && <span className="ml-auto">{channelRulesSlot}</span>}
    </div>
  );
}
